import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
// Removed unused Card imports
import { DataManagementList } from './DataManagementList';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Loader2, Settings2 } from 'lucide-react';
import { useSettings } from '../../services/SettingsContext';
import { createCapsifiService, ValueStream as CapsifiValueStream, ValueStreamDetails as CapsifiValueStreamDetails } from '../../services/CapsifiService';
import { transformValueStreamResponse, type ValueStreamResponse } from '../../services/valueStreamService';
import { PercentageMetrics } from '../OrbusStreamNavigatorComponent/lib/PercentageMetrics';
import { processCapabilities, processStageMetrics } from './dataManagementMetrics';
import CollapseLevelMenu from '../OrbusStreamNavigatorComponent/components/ProgressiveCollapse/CollapseLevelMenu';
import { usePostLoadHealthAlertsPanel } from '@/hooks/use-alerts-panel';

/**
 * DataManagement component with URL state sharing
 * 
 * State is synchronized with URL search parameters:
 * - `dataLevel`: number (0-4) - controls the data management level selection
 * 
 * Example URL: ?dataLevel=2
 * 
 * The state can be shared by copying the URL, and the component will restore
 * the data management level when loaded with those parameters.
 */

interface Application {
  id: string;
  label: string;
  metrics: PercentageMetrics;
}

interface Product {
  id: string;
  label: string;
}

interface Activity {
  id: string;
  label: string;
  metrics: PercentageMetrics;
}

interface Outcome {
  id: string;
  label: string;
  metrics: PercentageMetrics;
}

import { Skeleton } from '../ui/skeleton';

interface DataManagementProps {
  portalTargetId?: string;
}

interface TransformedStage {
  id: string;
  label: string;
  description: string;
  sortOrder: number;
  type: string;
  metrics: PercentageMetrics;
  capabilities: {
    id: string;
    name: string;
    timeDimension: string;
    type: string;
  }[];
}

type Capability = {
  id: string;
  label: string;
  type?: string;
  description?: string;
  stageId?: string;
  stageName?: string;
  metrics?: number;
  metricsObj: PercentageMetrics;
};

interface OrganisationUnit {
  id: string;
  label: string;
  type: string;
  metrics: PercentageMetrics;
}

interface TransformedValueStream {
  valueStream: {
    id: string;
    label: string;
    description: string | null;
    type: string;
    lanes?: Array<{
      id: string;
      label: string;
      stages: Array<{
        id: string;
        label: string;
        items: Array<{
          id: string;
          data?: {
            delegatedToOrganisation?: OrganisationUnit[];
          };
        }>;
      }>;
    }>;
  };
  stages: TransformedStage[];
}

export function DataManagement({ portalTargetId }: DataManagementProps = {}) {
  // All hooks must be called unconditionally at the top level
  const { apiSettings, isConfigured, setShowModelSelector } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params or defaults
  const getInitialDataLevel = () => {
    const param = searchParams.get('dataLevel');
    const level = param ? parseInt(param, 10) : 1;
    return isNaN(level) || level < 0 || level > 4 ? 1 : level;
  };
  
  const [selectedValue, setSelectedValue] = useState<number>(getInitialDataLevel);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [transformedDetails, setTransformedDetails] = useState<TransformedValueStream | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  
  // State for selected items
  const [selectedValueStream, setSelectedValueStream] = useState<string | null>(null);
  const [selectedStage, setSelectedStage] = useState<TransformedStage | null>(null);
  const [selectedCapability, setSelectedCapability] = useState<Capability | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<OrganisationUnit | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedProductActivity, setSelectedProductActivity] = useState<Activity | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState<Outcome | null>(null);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [capabilityTeams, setCapabilityTeams] = useState<OrganisationUnit[]>([]);
  const [teamApplications, setTeamApplications] = useState<Application[]>([]);
  const [isLoadingTeamApps, setIsLoadingTeamApps] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [productActivities, setProductActivities] = useState<Activity[]>([]);
  const [teams, setTeams] = useState<OrganisationUnit[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [applicationOutcomes, setApplicationOutcomes] = useState<Outcome[]>([]);
  const [applicationTeams, setApplicationTeams] = useState<OrganisationUnit[]>([]);
  const [applicationActivities, setApplicationActivities] = useState<Activity[]>([]);
  const [outcomeApplications, setOutcomeApplications] = useState<Application[]>([]);
  const [outcomeActivities, setOutcomeActivities] = useState<Activity[]>([]);
  const [activityProducts, setActivityProducts] = useState<Product[]>([]);
  const [activityApplications, setActivityApplications] = useState<Application[]>([]);
  const [activityOutcomes, setActivityOutcomes] = useState<Outcome[]>([]);

  // State for level selection (initialized above with URL params)
  const levelDescriptions = ['Operating Model', 'Organisation', 'Technology', 'Process & Data'];

  const portalTargetEl: Element | null = (() => {
    if (typeof document === 'undefined') return null;
    if (portalTargetId) {
      const el = document.getElementById(portalTargetId);
      if (el) return el;
    }
    return document.body;
  })();

  const visibleTabs = useMemo(() => {
    const tabs = ['value-streams'];
    
    // selectedValue 1: Operating Model - Value Stream Stage and Capability tabs visible
    if (selectedValue === 1) {
      tabs.push('value-stream-stages', 'business-capabilities');
    }
    // selectedValue 2: Organisation - only Team visible
    else if (selectedValue === 2) {
      tabs.push('teams');
    }
    // selectedValue 3: Technology - only Application visible
    else if (selectedValue === 3) {
      tabs.push('applications');
    }
    // selectedValue 4: Process & Data - Activity and Outcome visible
    else if (selectedValue === 4) {
      tabs.push('product-activities', 'outcomes');
    }
    
    return tabs;
  }, [selectedValue]);

  const tabLabels = {
    'value-streams': 'Value Streams',
    'value-stream-stages': 'Value Stream Stages',
    'business-capabilities': 'Business Capabilities',
    'teams': 'Teams',
    'applications': 'Applications',
    'products': 'Products',
    'product-activities': 'Activities',
    'outcomes': 'Outcomes',
  };

  // Check if we should fetch data
  const shouldFetchData = 
    Boolean(apiSettings.selectedModelId) && 
    Boolean(isConfigured);
  
  // Always initialize the service, but we'll control when it makes API calls
  const service = useMemo(() => 
    createCapsifiService(apiSettings.selectedModelId || ''), 
    [apiSettings.selectedModelId]
  );
  
  // Initialize state for valueStreams data
  const [valueStreams, setValueStreams] = useState<CapsifiValueStream[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Using shared processStageMetrics from dataManagementMetrics
  // Define the fetch function
  const fetchValueStreams = useCallback(async () => {
    // Don't make API calls if we don't have the required info
    if (!shouldFetchData) {
      console.debug('Missing required configuration - cannot fetch value streams');
      return;
    }

    // Limit the number of automatic retries to prevent infinite loops
    if (retryCount > 2) {
      console.debug('Maximum retry attempts reached, stopping automatic retries');
      setError('Maximum retry attempts reached. Please try manually refreshing or check server logs.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.debug(`Fetching value streams with model ID: ${apiSettings.selectedModelId}`);
      const data = await service.getValueStreams();
      setValueStreams(data);
      setSelectedValueStream(data[0].id);
      // Reset retry count on success
      setRetryCount(0);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch value streams';
      console.error('Error fetching value streams:', errorMessage);
      setError(errorMessage);
      
      // Special handling for the No credentials provided error
      if (errorMessage.includes('No credentials provided')) {
        console.debug('No credentials error detected - stopping retries');
        // Stop retrying on credential errors
        setRetryCount(999);
        return;
      }
      
      // Increment retry count for other errors
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  }, [apiSettings.selectedModelId, service, retryCount, shouldFetchData]);

  // Manual retry function
  const refetch = useCallback(() => {
    setRetryCount(0);
    fetchValueStreams();
  }, [fetchValueStreams]);

  // Fetch value streams when dependencies change
  useEffect(() => {
    if (shouldFetchData) {
      console.debug('Token and model ID available - initiating value streams fetch');
      fetchValueStreams();
    } else {
      console.debug('Missing required configuration - skipping value streams fetch');
    }
  }, [shouldFetchData, fetchValueStreams]);

  // Fetch value stream details when selected
  useEffect(() => {
    if (!selectedValueStream || !shouldFetchData) {
      setTransformedDetails(null);
      setTeams([]);
      setApplications([]);
      setProducts([]);
      setOutcomes([]);
      setProductActivities([]);
      return;
    }
    
    fetchValueStreamDetails(selectedValueStream);
  }, [selectedValueStream, shouldFetchData, service]);

  // Post-load health alerts - fires once when data is ready
  usePostLoadHealthAlertsPanel<TransformedStage, Capability>(
    transformedDetails?.stages,
    {
      getId: (s) => s.id,
      getName: (s) => s.label,
      getHealth: (s) =>
        typeof (s as any)?.PercentageMetrics?.HealthPercentage === 'number'
          ? (s as any).PercentageMetrics.HealthPercentage
          : typeof s?.metrics?.HealthPercentage === 'number'
          ? s.metrics.HealthPercentage
          : 0,
    },
    capabilities,
    {
      getId: (c) => c.id,
      getName: (c) => c.label,
      getHealth: (c) =>
        typeof c?.metricsObj?.HealthPercentage === 'number'
          ? c.metricsObj.HealthPercentage
          : 0,
    },
    !detailsLoading && (transformedDetails?.stages?.length ?? 0) > 0, // isDataReady
    {
      threshold: 75,
      stageTitle: 'Stage health alert',
      capabilityTitle: 'Capability health alert',
      maxItems: 12,
    }
  );

  function getTeams(
    valueStream: any,
    id?: string,
    idType?: 'capability' | 'application'
  ): OrganisationUnit[] {
    const teamsMap = new Map<string, OrganisationUnit>();
  
    // Validation: Only one type of filter may be provided
    if (id && !idType) {
      throw new Error("If you provide an id, you must specify idType as 'capability' or 'application'.");
    }
    if (!id && idType) {
      throw new Error("If you provide idType, you must also provide an id.");
    }
  
    // Traverse all lanes
    for (const lane of valueStream.lanes || []) {
      // Traverse all stages in each lane
      for (const stage of lane.stages || []) {
        // Traverse all items (capability instances) in each stage
        for (const item of stage.items || []) {
          const orgUnits = item.data?.delegatedToOrganisation || [];
          const applications = item.data?.deliveredByApplication || [];
  
          let matches = true;
          if (id && idType === 'capability') {
            matches = item.id === id;
          } else if (id && idType === 'application') {
            matches = applications.some((app: any) => app.id === id);
          }
  
          if (matches) {
            for (const unit of orgUnits) {
              if (!teamsMap.has(unit.id)) {
                teamsMap.set(unit.id, {
                  id: unit.id,
                  label: unit.label,
                  type: unit.type,
                  metrics: new PercentageMetrics()
                });
              }
            }
          }
        }
      }
    }
  
    return Array.from(teamsMap.values());
  }

  function getApplications(
    valueStream: any,
    id?: string,
    idType?: 'activity' | 'outcome' | 'team'
  ): Application[] {
    const appsMap = new Map<string, Application>();
  
    // Validation: Only one filter type at a time
    if (id && !idType) {
      throw new Error("If you provide an id, you must specify idType as 'activity', 'outcome', or 'team'.");
    }
    if (!id && idType) {
      throw new Error("If you provide idType, you must also provide an id.");
    }
  
    // Traverse all lanes
    for (const lane of valueStream.lanes || []) {
      for (const stage of lane.stages || []) {
        for (const item of stage.items || []) {
          const applications = item.data?.deliveredByApplication || [];
          const orgUnits = item.data?.delegatedToOrganisation || [];
          const enactedByArr = item.data?.enactedBy || [];
          const outcomes = item.data?.capabilityDesiredOutcome || [];
  
          let matches = true;
          if (id && idType === 'team') {
            matches = orgUnits.some((unit: any) => unit.id === id);
          } else if (id && idType === 'activity') {
            matches = enactedByArr.some((act: any) => act.id === id);
          } else if (id && idType === 'outcome') {
            matches = outcomes.some((out: any) => out.id === id);
          }
  
          if (matches) {
            for (const app of applications) {
              if (!appsMap.has(app.id)) {
                appsMap.set(app.id, {
                  id: app.id,
                  label: app.label,
                  metrics: new PercentageMetrics()
                });
              }
            }
          }
        }
      }
    }
    
    return Array.from(appsMap.values());
  }

  function getProducts(
    valueStream: any,
    activityId?: string
  ): Product[] {
    const productsMap = new Map<string, Product>();
  
    // Traverse all lanes
    for (const lane of valueStream.lanes || []) {
      for (const stage of lane.stages || []) {
        for (const item of stage.items || []) {
          const products = item.data?.addressedBy || [];
          const enactedByArr = item.data?.enactedBy || [];
  
          // If no activityId, always collect; else, only if this capability instance includes the activity
          const matches = !activityId || enactedByArr.some((act: any) => act.id === activityId);
  
          if (matches) {
            for (const product of products) {
              if (!productsMap.has(product.id)) {
                productsMap.set(product.id, {
                  id: product.id,
                  label: product.label,
                });
              }
            }
          }
        }
      }
    }
  
    return Array.from(productsMap.values());
  }

  function getOutcomes(
    valueStream: any,
    id?: string,
    idType?: 'application' | 'activity'
  ): Outcome[] {
    const outcomesMap = new Map<string, Outcome>();
  
    // Validation
    if (id && !idType) {
      throw new Error("If you provide an id, you must specify idType as 'application' or 'activity'.");
    }
    if (!id && idType) {
      throw new Error("If you provide idType, you must also provide an id.");
    }
  
    // Traverse all lanes
    for (const lane of valueStream.lanes || []) {
      for (const stage of lane.stages || []) {
        for (const item of stage.items || []) {
          const applications = item.data?.deliveredByApplication || [];
          const enactedByArr = item.data?.enactedBy || [];
          const outcomes = item.data?.capabilityDesiredOutcome || [];
  
          let matches = true;
          if (id && idType === 'application') {
            matches = applications.some((app: any) => app.id === id);
          } else if (id && idType === 'activity') {
            matches = enactedByArr.some((act: any) => act.id === id);
          }
  
          if (matches) {
            for (const outcome of outcomes) {
              if (!outcomesMap.has(outcome.id)) {
                outcomesMap.set(outcome.id, {
                  id: outcome.id,
                  label: outcome.label,
                  metrics: new PercentageMetrics()
                });
              }
            }
          }
        }
      }
    }
  
    return Array.from(outcomesMap.values());
  }

  // Function to extract product activities from value stream data
  function getCapabilities(
    valueStream: any,
    teamId?: string
  ): Capability[] {
    const capabilitiesMap = new Map<string, Capability>();

    // Traverse all lanes
    for (const lane of valueStream.lanes || []) {
      for (const stage of lane.stages || []) {
        for (const item of stage.items || []) {
          const orgUnits = item.data?.delegatedToOrganisation || [];
          const capabilities = item.instanceOf || [];

          // If no teamId, collect all; else, only if this capability instance includes the team
          const matchesTeam = !teamId || orgUnits.some((unit: any) => unit.id === teamId);

          if (matchesTeam) {
            for (const cap of capabilities) {
              if (!capabilitiesMap.has(cap.id)) {
                capabilitiesMap.set(cap.id, {
                  id: cap.id,
                  label: cap.label,
                  type: cap.type,
                  description: cap.description,
                  stageId: stage.id,
                  stageName: stage.label,
                  metricsObj: new PercentageMetrics()
                });
              }
            }
          }
        }
      }
    }

    return Array.from(capabilitiesMap.values());
  }

  function getActivity(
    valueStream: any,
    id?: string,
    idType?: 'product' | 'application' | 'outcome'
  ): Activity[] {
    const activityMap = new Map<string, Activity>();
  
    // Validation
    if (id && !idType) {
      throw new Error("If you provide an id, you must specify idType as 'product', 'application', or 'outcome'.");
    }
    if (!id && idType) {
      throw new Error("If you provide idType, you must also provide an id.");
    }
  
    // Traverse all lanes
    for (const lane of valueStream.lanes || []) {
      for (const stage of lane.stages || []) {
        for (const item of stage.items || []) {
          const enactedByArr = item.data?.enactedBy || [];
          const products = item.data?.addressedBy || [];
          const applications = item.data?.deliveredByApplication || [];
          const outcomes = item.data?.capabilityDesiredOutcome || [];
  
          let matches = true;
          if (id && idType === 'product') {
            matches = products.some((prod: any) => prod.id === id);
          } else if (id && idType === 'application') {
            matches = applications.some((app: any) => app.id === id);
          } else if (id && idType === 'outcome') {
            matches = outcomes.some((out: any) => out.id === id);
          }
  
          if (matches) {
            for (const act of enactedByArr) {
              if (!activityMap.has(act.id)) {
                activityMap.set(act.id, {
                  id: act.id,
                  label: act.label,
                  metrics: new PercentageMetrics()
                });
              }
            }
          }
        }
      }
    }
  
    return Array.from(activityMap.values());
  }

  const fetchValueStreamDetails = async (id: string) => {
    setDetailsLoading(true);
    setDetailsError(null);
    
    try {
      // Use the service instance to get the raw value stream data
      const data = await service.getValueStreamDetails(id);
      
      // Extract all data from the raw data before transformation
      const extractedTeams = getTeams(data);
      const extractedApplications = getApplications(data);
      const extractedCapabilities = getCapabilities(data);
      const extractedProducts = getProducts(data);
      const extractedOutcomes = getOutcomes(data);
      const extractedProductActivities = getActivity(data);
      setTeams(extractedTeams);
      setApplications(extractedApplications);
      setProducts(extractedProducts);
      setOutcomes(extractedOutcomes);
      setProductActivities(extractedProductActivities);
      
      // Process and set capabilities
      const processedCapabilities = processCapabilities(extractedCapabilities, data);
      setCapabilities(processedCapabilities);
      
      // Transform the data for the rest of the component
      const transformed = transformValueStreamResponse(data);
      
      // Process stage metrics based on the calculated capabilities
      const processedStages = processStageMetrics(transformed.stages, processedCapabilities);
      // Align with ValueStreamPerformanceMap by exposing PercentageMetrics on each stage
      const processedStagesWithPM = processedStages.map(s => ({
        ...s,
        PercentageMetrics: s.metrics,
      }));
      
      // Create the complete transformed data (without capabilities, as they are now in state)
      const completeTransformed: Omit<TransformedValueStream, 'capabilities'> & { capabilities: Capability[] } = {
        ...transformed,
        stages: processedStagesWithPM,
        capabilities: processedCapabilities,
      };
      
      setTransformedDetails(completeTransformed);
      
      // Update selected stage if it exists in new data
      if (selectedStage) {
        const newStage = completeTransformed.stages.find(s => s.id === selectedStage.id);
        setSelectedStage(newStage || null);
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to fetch value stream details.';
      setDetailsError(errorMessage);
      setTransformedDetails(null);
      
      // Prompt model re-selection if unauthorized
      if (errorMessage.includes('Unauthorized')) {
        setShowModelSelector(true);
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleValueStreamClick = (id: string) => {
    // Reset selected items when changing value streams
    setSelectedStage(null);
    setSelectedCapability(null);
    setCapabilityTeams([]);
    setSelectedValueStream(id);
  };

  const handleCapabilityClick = (capability: any) => {
    setSelectedCapability(capability);
    setSelectedStage(null);
    setSelectedTeam(null);
    
    // Get the raw value stream data to pass to getTeams
    if (selectedValueStream) {
      service.getValueStreamDetails(selectedValueStream)
        .then(data => {
          const teamsForCapability = getTeams(data, capability.id, 'capability');
          setCapabilityTeams(teamsForCapability);
        })
        .catch(() => {
          setCapabilityTeams([]);
        });
    } else {
      setCapabilityTeams([]);
    }
  };

  // Sync state to URL when it changes (only after initialization)
  useEffect(() => {
    if (!isInitialized) return;
    
    // Read current URL params at update time to avoid race conditions
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('dataLevel', selectedValue.toString());
    setSearchParams(currentParams, { replace: true });
  }, [selectedValue, isInitialized, setSearchParams]);

  // Apply data level from URL on mount
  useEffect(() => {
    if (isInitialized) return;
    
    const urlLevel = getInitialDataLevel();
    setSelectedValue(urlLevel);
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show configuration prompt if not configured
  if (!isConfigured || !apiSettings.selectedModelId) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-muted-foreground mb-4">
          Please configure your settings to get started.
        </p>
        <Button onClick={() => setShowModelSelector(true)}>
          Configure Settings
        </Button>
      </div>
    );
  }
  
  // Don't try to render with the hook result until we're sure we should fetch data
  if (!shouldFetchData) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-muted-foreground mb-4">
          Loading configuration...
        </p>
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={refetch} variant="outline" size="sm" className="gap-2">
          <Settings2 className="h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {portalTargetId && portalTargetEl ? (
        createPortal(
          <div className="nlc-collapse">
            <div className="vertical-collapse">
              <CollapseLevelMenu
                visible={true}
                selectedValue={selectedValue}
                onChange={setSelectedValue}
                items={levelDescriptions}
                title="Data Management Level"
              />
            </div>
          </div>,
          portalTargetEl
        )
      ) : (
        <CollapseLevelMenu
          visible={true}
          selectedValue={selectedValue}
          onChange={setSelectedValue}
          items={levelDescriptions}
          title="Data Management Level"
        />
      )}
      <Tabs defaultValue="value-streams">
        <TabsList>
          {visibleTabs.map(tab => (
            <TabsTrigger key={tab} value={tab}>
              {tabLabels[tab as keyof typeof tabLabels]}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="value-streams" className="space-y-4">
          <DataManagementList
            loading={loading}
            items={valueStreams.map(vs => ({
              ...vs,
              description: typeof vs.description === 'undefined' ? null : vs.description
            }))}
            selectedId={selectedValueStream}
            onItemClick={handleValueStreamClick}
            columns={[
              { key: 'label', header: 'Title' },
              { key: 'description', header: 'Description' },
            ]}
          />
        </TabsContent>

      {visibleTabs.includes('value-stream-stages') && (
        <TabsContent value="value-stream-stages" className="space-y-4">
          <DataManagementList
            loading={detailsLoading || isLoadingTeamApps}
            items={transformedDetails?.stages || []}
            selectedId={selectedStage?.id || null}
            onItemClick={(id) => {
              const stage = transformedDetails?.stages.find((s) => s.id === id);
              setSelectedStage(stage || null);
              setSelectedCapability(null);
            }}
            columns={[
              { key: 'label', header: 'Title' },
              { key: 'description', header: 'Description' },
              { key: 'sortOrder', header: 'Order' },
            ]}
            selectedItem={selectedStage}
            itemType="stage"
          />
        </TabsContent>
      )}

      {visibleTabs.includes('business-capabilities') && (
        <TabsContent value="business-capabilities" className="space-y-4">
          <DataManagementList
            loading={detailsLoading || isLoadingTeamApps}
            items={capabilities}
            selectedId={selectedCapability?.id || null}
            onItemClick={(id) => {
              const capability = capabilities.find((c) => c.id === id);
              if (capability) {
                handleCapabilityClick(capability);
              }
            }}
            columns={[
              { key: 'label', header: 'Title' },
              { key: 'stageName', header: 'Stage' },
              { key: 'description', header: 'Description' },
            ]}
            selectedItem={selectedCapability}
            itemType="capability"
            relatedItems={selectedCapability && capabilityTeams.length > 0 ? [{
              title: 'Related Teams',
              items: capabilityTeams,
              columns: [
                { key: 'label', header: 'Team Name' },
                { key: 'type', header: 'Type' },
              ]
            }] : undefined}
          />
        </TabsContent>
      )}

      {visibleTabs.includes('teams') && (
        <TabsContent value="teams" className="space-y-4">
          <DataManagementList
            loading={detailsLoading || isLoadingTeamApps}
            items={teams}
            selectedId={selectedTeam?.id || null}
            onItemClick={(id) => {
              const team = teams.find(t => t.id === id);
              if (team) {
                setSelectedTeam(team);
                // Show loading state
                setIsLoadingTeamApps(true);
                
                // Get the raw value stream data to pass to getApplications and getCapabilitiesForTeam
                if (selectedValueStream) {
                  service.getValueStreamDetails(selectedValueStream)
                    .then(data => {
                      const appsForTeam = getApplications(data, team.id, 'team');
                      setTeamApplications(appsForTeam);
                      const capabilitiesForTeam = getCapabilities(data, team.id);
                      setCapabilities(capabilitiesForTeam);
                    })
                    .catch(() => {
                      setTeamApplications([]);
                      setCapabilities([]);
                    })
                    .finally(() => {
                      setIsLoadingTeamApps(false);
                    });
                } else {
                  setTeamApplications([]);
                  setCapabilities([]);
                  setIsLoadingTeamApps(false);
                }
              } else {
                setSelectedTeam(null);
                setTeamApplications([]);
                setCapabilities([]);
                setIsLoadingTeamApps(false);
              }
            }}
            columns={[
              { key: 'label', header: 'Name' },
              { key: 'id', header: 'ID' },
            ]}
            selectedItem={selectedTeam}
            itemType="team"
            relatedItems={[
              {
                title: 'Related Applications',
                items: teamApplications,
                columns: [
                  { key: 'label', header: 'Name' },
                  { key: 'id', header: 'ID' }
                ]
              },
              {
                title: 'Related Capabilities',
                items: capabilities,
                columns: [
                  { key: 'label', header: 'Name' },
                  { key: 'stageName', header: 'Stage' },
                  { key: 'description', header: 'Description' },
                ]
              }
            ].filter(group => group.items.length > 0)}
          />
        </TabsContent>
      )}

      {visibleTabs.includes('applications') && (
        <TabsContent value="applications" className="space-y-4">
          <DataManagementList
            loading={detailsLoading || isLoadingTeamApps}
            items={applications}
            selectedId={selectedApplication?.id || null}
            onItemClick={(id) => {
              const app = applications.find((a) => a.id === id);
              if (app) {
                setSelectedApplication(app);
                setIsLoadingTeamApps(true);
                if (selectedValueStream) {
                  service
                    .getValueStreamDetails(selectedValueStream)
                    .then((data) => {
                      const outcomes = getOutcomes(data, app.id, 'application');
                      setApplicationOutcomes(outcomes);
                      const teams = getTeams(data, app.id, 'application');
                      setApplicationTeams(teams);
                      const activities = getActivity(data, app.id, 'application');
                      setApplicationActivities(activities);
                    })
                    .catch(() => {
                      setApplicationOutcomes([]);
                      setApplicationTeams([]);
                      setApplicationActivities([]);
                    })
                    .finally(() => {
                      setIsLoadingTeamApps(false);
                    });
                } else {
                  setApplicationOutcomes([]);
                  setApplicationTeams([]);
                }
              } else {
                setSelectedApplication(null);
                setApplicationOutcomes([]);
                setApplicationTeams([]);
              }
            }}
            columns={[
              { key: 'label', header: 'Name' },
              { key: 'id', header: 'ID' },
            ]}
            selectedItem={selectedApplication}
            itemType="application"
            relatedItems={(() => {
              return [
                {
                  title: 'Outcomes',
                  items: applicationOutcomes,
                  columns: [{ key: 'label', header: 'Outcome' }],
                },
                {
                  title: 'Teams',
                  items: applicationTeams,
                  columns: [{ key: 'label', header: 'Team' }],
                },
                {
                  title: 'Activities',
                  items: applicationActivities,
                  columns: [{ key: 'label', header: 'Activity' }],
                },
              ];
            })()}
          />
        </TabsContent>
      )}

      {visibleTabs.includes('products') && (
        <TabsContent value="products" className="space-y-4">
          <DataManagementList
            loading={detailsLoading || isLoadingTeamApps}
            items={products}
            selectedId={selectedProduct?.id || null}
            onItemClick={(id) => {
              const product = products.find(p => p.id === id);
              setSelectedProduct(product || null);
              
              if (product && selectedValueStream) {
                // Show loading state
                setIsLoadingTeamApps(true);
                
                // Get the raw value stream data directly from the service
                service.getValueStreamDetails(selectedValueStream)
                  .then(data => {
                    // Fetch related activities for the selected product
                    const activities = getActivity(data, product.id, 'product');
                    setProductActivities(activities);
                    
                    setIsLoadingTeamApps(false);
                  })
                  .catch(() => {
                    console.debug('Error fetching value stream details');
                    setProductActivities([]);
                    setIsLoadingTeamApps(false);
                  });
              } else {
                setProductActivities([]);
              }
            }}
            columns={[
              { key: 'label', header: 'Name' },
              { key: 'id', header: 'ID' },
            ]}
            selectedItem={selectedProduct}
            itemType="product"
            relatedItems={selectedProduct && productActivities.length > 0 ? [
              {
                title: 'Activities',
                items: productActivities,
                columns: [{ key: 'label', header: 'Activity' }],
              }
            ] : undefined}
          />
        </TabsContent>
      )}

      {visibleTabs.includes('product-activities') && (
        <TabsContent value="product-activities" className="space-y-4">
          <DataManagementList
            loading={detailsLoading || isLoadingTeamApps}
            items={productActivities}
            selectedId={selectedProductActivity?.id || null}
            onItemClick={(id) => {
              const activity = productActivities.find(a => a.id === id);
              setSelectedProductActivity(activity || null);
              
              if (activity && selectedValueStream) {
                // Show loading state
                setIsLoadingTeamApps(true);
                
                // Get the raw value stream data directly from the service
                service.getValueStreamDetails(selectedValueStream)
                  .then(data => {
                    // Fetch related products for the selected activity
                    const products = getProducts(data, activity.id);
                    setActivityProducts(products);
                    
                    // Fetch related applications for the selected activity
                    const applications = getApplications(data, activity.id, 'activity');
                    setActivityApplications(applications);
                    
                    // Fetch related outcomes for the selected activity
                    const outcomes = getOutcomes(data, activity.id, 'activity');
                    setActivityOutcomes(outcomes);
                    
                    setIsLoadingTeamApps(false);
                  })
                  .catch(() => {
                    console.debug('Error fetching value stream details');
                    setActivityProducts([]);
                    setActivityApplications([]);
                    setActivityOutcomes([]);
                    setIsLoadingTeamApps(false);
                  });
              } else {
                setActivityProducts([]);
                setActivityApplications([]);
                setActivityOutcomes([]);
              }
            }}
            columns={[
              { key: 'label', header: 'Name' },
              { key: 'id', header: 'ID' },
            ]}
            selectedItem={selectedProductActivity}
            itemType="activity"
            relatedItems={selectedProductActivity ? [
              ...(activityProducts.length > 0 ? [{
                title: 'Products',
                items: activityProducts,
                columns: [{ key: 'label', header: 'Product' }],
              }] : []),
              ...(activityApplications.length > 0 ? [{
                title: 'Applications',
                items: activityApplications,
                columns: [{ key: 'label', header: 'Application' }],
              }] : []),
              ...(activityOutcomes.length > 0 ? [{
                title: 'Outcomes',
                items: activityOutcomes,
                columns: [{ key: 'label', header: 'Outcome' }],
              }] : [])
            ] : undefined}
          />
        </TabsContent>
      )}

      {visibleTabs.includes('outcomes') && (
        <TabsContent value="outcomes" className="space-y-4">
          <DataManagementList
            loading={detailsLoading || isLoadingTeamApps}
            items={outcomes}
            selectedId={selectedOutcome?.id || null}
            onItemClick={(id) => {
              const state = outcomes.find(s => s.id === id);
              setSelectedOutcome(state || null);
              if (state && selectedValueStream) {
                // Show loading state
                setIsLoadingTeamApps(true);
                
                // Get the raw value stream data directly from the service
                service.getValueStreamDetails(selectedValueStream)
                  .then(data => {
                    console.debug('Selected outcome ID:', state.id);
                    
                    // Fetch related applications for the selected outcome
                    const apps = getApplications(data, state.id, 'outcome');
                    console.debug('Related applications for outcome:', apps);
                    setOutcomeApplications(apps);
                    
                    // Fetch related activities for the selected outcome
                    const activities = getActivity(data, state.id, 'outcome');
                    console.debug('Related activities for outcome:', activities);
                    setOutcomeActivities(activities);
                    
                    setIsLoadingTeamApps(false);
                  })
                  .catch(() => {
                    console.debug('Error fetching value stream details');
                    setOutcomeApplications([]);
                    setOutcomeActivities([]);
                    setIsLoadingTeamApps(false);
                  });
              } else {
                console.debug('No outcome selected or no selected value stream');
                setOutcomeApplications([]);
                setOutcomeActivities([]);
              }
            }}
            columns={[
              { key: 'label', header: 'Name' },
            ]}
            selectedItem={selectedOutcome}
            itemType="outcome"
            relatedItems={[
              {
                title: 'Applications',
                items: outcomeApplications,
                columns: [{ key: 'label', header: 'Application' }],
              },
              {
                title: 'Activities',
                items: outcomeActivities,
                columns: [{ key: 'label', header: 'Activity' }],
              }
            ]}
          />
        </TabsContent>
      )}
    </Tabs>
  </div>
  );
}
